<template>
  <nav
    class="fixed top-0 z-50 w-full transition-all duration-300"
    :class="{ 'backdrop-blur-md': isScrolled || showMobileMenu, 'backdrop-brightness-50': showMobileMenu }"
  >
    <div class="mx-auto max-w-[85rem] px-4">
      <div class="flex h-20 items-center justify-between">
        <!-- Logo -->
        <InertiaLink href="/" class="flex items-center space-x-3" @click="closeMobileMenu">
          <img src="@images/logo/vibeteams-logo-secondary.svg" alt="Vibe Teams Logo" class="h-8" loading="lazy" />
        </InertiaLink>

        <!-- Center Menu -->
        <div class="hidden items-center space-x-3 md:flex">
          <template v-for="item in menuItems" :key="item.name">
            <InertiaLink
              v-if="item.href.startsWith('/')"
              :href="item.href"
              class="cursor-pointer py-2 font-segoe-bold text-base text-nowrap uppercase transition-colors duration-300 hover:text-mauve-500 md:text-sm lg:px-3"
            >
              {{ item.name }}
            </InertiaLink>
            <button
              v-else
              type="button"
              class="cursor-pointer py-2 font-segoe-bold text-base text-nowrap uppercase transition-colors duration-300 hover:text-mauve-500 md:text-sm lg:px-3"
              @click="scrollToSection(item.href)"
            >
              {{ item.name }}
            </button>
          </template>
        </div>

        <div class="flex items-center space-x-3">
          <UiButton v-if="$page.props.auth.user" variant="gradient" size="default" as-child class="hidden uppercase md:grid">
            <InertiaLink :href="dashboardRoute" @click="closeMobileMenu"> Dashboard </InertiaLink>
          </UiButton>

          <div v-else class="flex gap-3">
            <!-- Sign In Button -->
            <div class="hidden items-center space-x-3 md:flex">
              <UiButton variant="gradient" size="default" as-child class="uppercase">
                <InertiaLink :href="route('login')"> sign in </InertiaLink>
              </UiButton>
            </div>

            <!-- Sign Up Button -->
            <div class="hidden items-center space-x-3 md:flex">
              <UiButton variant="secondaryOutline" size="default" as-child class="uppercase hover:text-mauve-500">
                <!-- Using regular anchor tag for full page refresh in order to load reCaptcha -->
                <a :href="route('register')"> sign up </a>
              </UiButton>
            </div>
          </div>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden">
          <button type="button" class="text-white focus:outline-none" aria-label="Toggle menu" @click="toggleMobileMenu">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div v-if="showMobileMenu" class="h-[calc(100dvh-80px)] border-t border-gray-200 md:hidden">
        <div class="flex h-full flex-col justify-between px-2 pt-2 pb-3">
          <div class="space-y-3">
            <template v-for="item in menuItems" :key="item.name">
              <InertiaLink
                v-if="item.href.startsWith('/')"
                :href="item.href"
                class="block rounded-md px-3 py-2 font-segoe-bold text-base text-white uppercase transition-colors duration-200 hover:bg-mauve-600 hover:text-white"
                @click="closeMobileMenu"
              >
                {{ item.name }}
              </InertiaLink>
              <button
                v-else
                type="button"
                class="block w-full rounded-md px-3 py-2 text-left font-segoe-bold text-base text-white uppercase transition-colors duration-200 hover:bg-mauve-600 hover:text-white"
                @click="
                  scrollToSection(item.href);
                  closeMobileMenu();
                "
              >
                {{ item.name }}
              </button>
            </template>
          </div>

          <div class="mt-3 grid gap-2 border-t border-gray-200 pt-3">
            <UiButton v-if="$page.props.auth.user" variant="gradient" size="lg" as-child class="w-full">
              <InertiaLink :href="dashboardRoute" @click="closeMobileMenu"> Dashboard </InertiaLink>
            </UiButton>
            <div v-else class="grid gap-2">
              <UiButton variant="gradient" size="lg" as-child class="w-full uppercase">
                <InertiaLink :href="route('login')" @click="closeMobileMenu"> Sign In </InertiaLink>
              </UiButton>
              <UiButton variant="secondaryOutline" size="lg" as-child class="w-full uppercase hover:text-mauve-500">
                <a :href="route('register')" @click="closeMobileMenu"> Sign up </a>
              </UiButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { Button as UiButton } from '@/components/ui/button';
import { Link } from '@inertiajs/vue3';

export default {
  name: 'Navbar',
  components: {
    InertiaLink: Link,
    UiButton,
  },
  data() {
    return {
      showMobileMenu: false,
      isScrolled: false,
      menuItems: [
        { name: 'Home', href: '#hero' },
        { name: 'About', href: '#aboutUs' },
        { name: 'How it Works', href: '#howItWorks' },
        { name: 'Pricing', href: '#pricing' },
      ],
    };
  },
  computed: {
    dashboardRoute() {
      const user = this.$page.props.auth.user;
      if (!user) return this.route('team-creator');

      const teamsCount = user.teams?.length || 0;
      return teamsCount > 0 ? this.route('teams.page') : this.route('team-creator');
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleScroll() {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrolled / (documentHeight - windowHeight)) * 100;

      this.isScrolled = scrollPercent > 1;
    },
    handleResize() {
      // Close mobile menu and remove overflow-hidden when resizing to desktop
      if (window.innerWidth >= 768 && this.showMobileMenu) {
        // 768px md breakpoint
        this.closeMobileMenu();
      }
    },
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;

      // Toggle overflow-hidden on html to prevent background scrolling
      if (this.showMobileMenu) {
        document.documentElement.classList.add('overflow-hidden');
      } else {
        document.documentElement.classList.remove('overflow-hidden');
      }
    },
    closeMobileMenu() {
      if (this.showMobileMenu) {
        this.showMobileMenu = false;
        document.documentElement.classList.remove('overflow-hidden');
      }
    },
    scrollToSection(sectionId: string) {
      // Remove the # from the sectionId if it exists
      const targetId = sectionId.startsWith('#') ? sectionId.slice(1) : sectionId;
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        const navbarHeight = 100; // Height of the fixed navbar, adjust lang as needed
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    },
  },
};
</script>
